import {Incoterm, type IncotermData} from "@/types/order-entry/Incoterm";
import {Package, type PackageData} from "@/types/order-entry/Package";
import {OrderEntryFeature, type OrderEntryFeatureData} from "@/types/order-entry/OrderEntryFeature";

export class InternationalRoadOrderEntry {
    public id:number|null;
    public company_id:number;
    public adr:number;
    public labels:number;
    public loading_time_interval:number;
    public min_loading_time:string|null;
    public max_loading_time:string|null;
    public min_load_interval:string|null;
    public loading_day_tomorrow:number;
    public unloading_time_interval:number;
    public min_unloading_time:string|null;
    public max_unloading_time:string|null;
    public min_unload_interval:string|null;
    public dates_not_compliant: number;
    public detailed_track_trace:number;
    public track_trace_link:number;
    public client_emails:string | null;
    public send_client_emails: number;
    public klg_emails:string | null;
    public send_klg_emails: number;
    public incoterms: Incoterm[];
    public packages: Package[];
    public orderEntryFeatures: OrderEntryFeature[];

    constructor(data:InternationalRoadOrderEntryData|void) {
        if(data === undefined)
        {
            this.id = null;
            this.company_id = 0;
            this.adr = 0;
            this.labels = 0;
            this.loading_time_interval = 1;
            this.min_loading_time = "";
            this.max_loading_time = "";
            this.min_load_interval = "";
            this.loading_day_tomorrow = 1;
            this.unloading_time_interval = 1;
            this.min_unloading_time = "";
            this.max_unloading_time = "";
            this.min_unload_interval = "";
            this.dates_not_compliant = 0;
            this.detailed_track_trace = 1;
            this.track_trace_link = 0;
            this.client_emails = null;
            this.send_client_emails = 1;
            this.klg_emails = null;
            this.send_klg_emails = 1;
            this.incoterms = [];
            this.packages = [];
            this.orderEntryFeatures = [];
        }
        else
        {
            this.id = data.id;
            this.company_id = data.company_id;
            this.adr = data.adr;
            this.labels = data.labels;
            this.loading_time_interval = data.loading_time_interval;
            this.min_loading_time = data.min_loading_time;
            this.max_loading_time = data.max_loading_time;
            this.min_load_interval = data.min_load_interval;
            this.loading_day_tomorrow = data.loading_day_tomorrow;
            this.unloading_time_interval = data.unloading_time_interval;
            this.min_unloading_time = data.min_unloading_time;
            this.max_unloading_time = data.max_unloading_time;
            this.min_unload_interval = data.min_unload_interval;
            this.dates_not_compliant = data.dates_not_compliant;
            this.detailed_track_trace = data.detailed_track_trace;
            this.track_trace_link = data.track_trace_link;
            this.client_emails = data.client_emails;
            this.send_client_emails = data.send_client_emails;
            this.klg_emails = data.klg_emails;
            this.send_klg_emails = data.send_klg_emails;
            this.incoterms = [];
            if (data.incoterms !== undefined) {
                data.incoterms.forEach(incoterm => this.incoterms.push((new Incoterm(incoterm))));
            }

            this.packages = [];
            if (data.packages !== undefined) {
                data.packages.forEach(pack => this.packages.push((new Package(pack))));
            }

            this.orderEntryFeatures = [];
            if (data.orderEntryFeatures !== undefined) {
                data.orderEntryFeatures.forEach(orderEntryFeature => this.orderEntryFeatures.push((new OrderEntryFeature(orderEntryFeature))));
            }
        }
    }

    serializeData(without_relations: boolean | Array<string> = false) {
        const serialized_data = {

            id: this.id,
            company_id: this.company_id,
            adr: this.adr,
            labels: this.labels,
            loading_time_interval: this.loading_time_interval,
            min_loading_time: this.min_loading_time,
            max_loading_time: this.max_loading_time,
            min_load_interval: this.min_load_interval,
            loading_day_tomorrow: this.loading_day_tomorrow,
            unloading_time_interval: this.unloading_time_interval,
            min_unloading_time: this.min_unloading_time,
            max_unloading_time: this.max_unloading_time,
            min_unload_interval: this.min_unload_interval,
            dates_not_compliant: this.dates_not_compliant,
            detailed_track_trace: this.detailed_track_trace,
            track_trace_link: this.track_trace_link,
            client_emails: this.client_emails,
            send_client_emails: this.send_client_emails,
            klg_emails: this.klg_emails,
            send_klg_emails: this.send_klg_emails,
        }
        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('incoterms')
            )
        ) {
            serialized_data.incoterms = [];
            this.incoterms.forEach(el => {
                serialized_data.incoterms.push(el.serializeData())
            })
        }
        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('packages')
            )
        ) {
            serialized_data.packages = [];
            this.packages.forEach(el => {
                if (el instanceof Package && typeof el.serializeData === "function") {
                    serialized_data.packages.push(el.serializeData())
                } else {
                    serialized_data.packages.push(el)
                }
            })
        }
        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('orderEntryFeatures')
            )
        ) {
            serialized_data.orderEntryFeatures = [];
            this.orderEntryFeatures.forEach(el => {
                if (el instanceof OrderEntryFeature && typeof el.serializeData === "function") {
                    serialized_data.orderEntryFeatures.push(el.serializeData())
                } else {
                    serialized_data.orderEntryFeatures.push(el)
                }
            })
        }

        return serialized_data;
    }
}

export type InternationalRoadOrderEntryData = {
    id:number|null;
    company_id:number;
    adr:number;
    labels:number;
    loading_time_interval:number;
    min_loading_time:string|null;
    max_loading_time:string|null;
    min_load_interval:string|null;
    loading_day_tomorrow:number;
    unloading_time_interval:number;
    min_unloading_time:string|null;
    max_unloading_time:string|null;
    min_unload_interval:string|null;
    dates_not_compliant:number;
    detailed_track_trace:number;
    track_trace_link:number;
    client_emails:string | null;
    send_client_emails: number;
    klg_emails:string | null;
    send_klg_emails: number;
    incoterms: IncotermData[];
    packages: PackageData[];
    orderEntryFeatures: OrderEntryFeatureData[];
}